import React, { useCallback, useEffect, useState } from "react";
import { FaMapMarkerAlt, FaPlus } from "react-icons/fa";
import { useAuth } from "../../../context/AuthContext";
import { useGoogleMaps } from "../../../context/GoogleMapsContext";
import { useOrganization } from "../../../context/OrganizationContext";
import { usePermissions } from "../../../context/PermissionsContext";
import { supabase } from "../../../supabaseClient";
import {
  listOrganizationBowlingCenters,
  unlinkOrganizationBowlingCenter,
} from "../../../utility/organizationBowlingCenters";
import AddCenterForm from "../../TournamentLocations/AddCenterForm";
import SelectedCenters from "../../TournamentLocations/SelectedCenters";
import InlineAlert from "../../UI/DesignStandards/Notifications/InlineAlert";
import { PrimaryButton, SkeletonBoard } from "../../UI/DesignStandards";

const OrganizationBowlingCenters = () => {
  const { user } = useAuth();
  const { isLoaded, loadError } = useGoogleMaps();
  const { currentOrganization } = useOrganization();
  const { canManageTournaments, canManageOrganization } = usePermissions();
  const canEdit = canManageTournaments || canManageOrganization;

  const [centers, setCenters] = useState([]);
  const [allCenters, setAllCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");

  const organizationId = currentOrganization?.id;

  const loadCenters = useCallback(async () => {
    if (!organizationId) {
      setCenters([]);
      setLoading(false);
      return;
    }
    setError("");
    try {
      const [orgCenters, catalog] = await Promise.all([
        listOrganizationBowlingCenters(organizationId),
        supabase
          .from("bowlingCenters")
          .select("*")
          .is("deletedAt", null)
          .then(({ data, error: catalogError }) => {
            if (catalogError) throw catalogError;
            return data || [];
          }),
      ]);
      setCenters(orgCenters);
      setAllCenters(catalog);
    } catch (err) {
      console.error(err);
      setError("Could not load bowling centers.");
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    setLoading(true);
    loadCenters();
  }, [loadCenters]);

  const handleCenterAdded = async () => {
    await loadCenters();
    setShowForm(false);
  };

  const handleRemove = async (bowlingCenterId) => {
    if (!organizationId || !bowlingCenterId) return;
    setError("");
    try {
      await unlinkOrganizationBowlingCenter({
        organizationId,
        bowlingCenterId,
      });
      await loadCenters();
    } catch (err) {
      console.error(err);
      setError("Could not remove that bowling center.");
    }
  };

  if (loadError) {
    return (
      <InlineAlert
        type="error"
        title="There was a problem"
        message="Could not load Google Maps. Refresh the page and try again."
      />
    );
  }

  if (!isLoaded || loading) {
    return (
      <div className="space-y-4 sm:space-y-6" role="status" aria-label="Loading bowling centers">
        <SkeletonBoard rows={2} />
        <SkeletonBoard header={false} rows={4} />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <header className="card-board overflow-hidden">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
              Bowling centers
            </h1>
            <p className="text-sm text-brand-mist mt-1">
              Bowling centers this organization uses.
            </p>
          </div>
          {canEdit && !showForm ? (
            <PrimaryButton onClick={() => setShowForm(true)} icon={FaPlus}>
              Add bowling center
            </PrimaryButton>
          ) : null}
        </div>
      </header>

      {error ? (
        <InlineAlert
          type="error"
          title="There was a problem"
          message={error}
          onClose={() => setError("")}
        />
      ) : null}

      {centers.length === 0 && !showForm ? (
        <div className="card-board p-8 text-center">
          <FaMapMarkerAlt className="mx-auto h-10 w-10 text-brand-muted mb-3" />
          <h2 className="heading-display text-lg mb-2">
            No bowling centers yet
          </h2>
          <p className="text-brand-muted mb-4">
            Add the house you bowl at. You can add another later if you travel
            or use a second center.
          </p>
          {canEdit ? (
            <PrimaryButton onClick={() => setShowForm(true)} icon={FaPlus}>
              Add bowling center
            </PrimaryButton>
          ) : null}
        </div>
      ) : (
        <div className="card-board p-6">
          {centers.length > 0 ? (
            <SelectedCenters
              selectedCenters={centers}
              onRemoveCenter={handleRemove}
              isLoggedIn={!!user}
              canManage={canEdit}
            />
          ) : null}
          <AddCenterForm
            showForm={showForm}
            onToggleForm={() => setShowForm(!showForm)}
            allCenters={allCenters}
            organizationId={organizationId}
            onCenterAdded={handleCenterAdded}
            isLoggedIn={!!user}
            canManage={canEdit}
            hideToggle
          />
        </div>
      )}
    </div>
  );
};

export default OrganizationBowlingCenters;
